import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/shadcn/command'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { cn } from '@/lib/utils'
import { IconCheckMarkOutline } from '@icons/IconCheckMarkOutline'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { IconDownChevronOutline } from '@icons/IconDownChevronOutline'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Control, FieldPath, FieldValues } from 'react-hook-form'

export interface SelectOption<T = string | number> {
  value: T
  label: string
  description?: string
  group?: string
}

interface MultiSelectComboboxProps<T = string | number> {
  options: SelectOption<T>[]
  selectedValues: T[]
  onSelectionChange: (selectedValues: T[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  triggerClassName?: string
  renderBadgesInTrigger?: boolean
  disabled?: boolean
}

export function MultiSelectCombobox<T extends string | number>({
  options,
  selectedValues,
  onSelectionChange,
  placeholder = 'Select items...',
  searchPlaceholder = 'Search items...',
  emptyMessage = 'No items found.',
  className,
  triggerClassName,
  renderBadgesInTrigger = false,
  disabled = false,
}: MultiSelectComboboxProps<T>) {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [triggerWidth, setTriggerWidth] = useState<number>(0)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Filter options based on search
  const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()))
  const hasOptionGroups = filteredOptions.some((option) => !!option.group)
  const groupedFilteredOptions = useMemo(() => {
    const groups = new Map<string, SelectOption<T>[]>()
    for (const option of filteredOptions) {
      const groupName = option.group || 'Other'
      const current = groups.get(groupName) ?? []
      current.push(option)
      groups.set(groupName, current)
    }
    return Array.from(groups.entries())
  }, [filteredOptions])

  // Get selected options for display
  const selectedOptions = options.filter((option) => selectedValues.includes(option.value))

  const handleSelect = (value: T) => {
    const isSelected = selectedValues.includes(value)
    let newSelection: T[]

    if (isSelected) {
      // Remove from selection
      newSelection = selectedValues.filter((v) => v !== value)
    } else {
      // Add to selection
      newSelection = [...selectedValues, value]
    }

    onSelectionChange(newSelection)
  }

  const handleRemove = (value: T) => {
    const newSelection = selectedValues.filter((v) => v !== value)
    onSelectionChange(newSelection)
  }

  // Update trigger width when component mounts or when the popover opens
  useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth)
    }
  }, [open])

  return (
    <div className={cn('space-y-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            variant="input"
            role="combobox"
            aria-expanded={open}
            className={cn('w-full justify-between gap-2', triggerClassName)}
            disabled={disabled}
          >
            {selectedOptions.length > 0 ? (
              renderBadgesInTrigger ? (
                <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                  {selectedOptions.slice(0, 2).map((option) => (
                    <Badge
                      key={String(option.value)}
                      variant="secondary"
                      className={cn('min-w-0', selectedOptions.length === 1 ? 'max-w-full' : 'max-w-[45%]')}
                      title={option.label}
                    >
                      <span className="block min-w-0 truncate">{option.label}</span>
                    </Badge>
                  ))}
                  {selectedOptions.length > 2 && (
                    <Badge
                      variant="secondary"
                      className="shrink-0"
                      title={selectedOptions
                        .slice(2)
                        .map((option) => option.label)
                        .join(', ')}
                    >
                      +{selectedOptions.length - 2}
                    </Badge>
                  )}
                </div>
              ) : (
                `${selectedOptions.length} item${selectedOptions.length > 1 ? 's' : ''} selected`
              )
            ) : (
              <span className="truncate text-muted-foreground">{placeholder}</span>
            )}
            <IconDownChevronOutline className="size-3 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0" style={{ width: triggerWidth ? `${triggerWidth}px` : 'auto' }} align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} value={searchValue} onValueChange={setSearchValue} />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              {hasOptionGroups ? (
                groupedFilteredOptions.map(([groupName, groupOptions]) => (
                  <CommandGroup key={groupName} heading={groupName}>
                    {groupOptions.map((option) => {
                      const isSelected = selectedValues.includes(option.value)
                      return (
                        <CommandItem
                          key={String(option.value)}
                          value={option.label}
                          onSelect={() => handleSelect(option.value)}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{option.label}</span>
                            {option.description && (
                              <span className="text-xs text-muted-foreground">{option.description}</span>
                            )}
                          </div>
                          <IconCheckMarkOutline className={cn('ml-auto', isSelected ? 'opacity-100' : 'opacity-0')} />
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                ))
              ) : (
                <CommandGroup>
                  {filteredOptions.map((option) => {
                    const isSelected = selectedValues.includes(option.value)
                    return (
                      <CommandItem
                        key={String(option.value)}
                        value={option.label}
                        onSelect={() => handleSelect(option.value)}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{option.label}</span>
                          {option.description && (
                            <span className="text-xs text-muted-foreground">{option.description}</span>
                          )}
                        </div>
                        <IconCheckMarkOutline className={cn('ml-auto', isSelected ? 'opacity-100' : 'opacity-0')} />
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Display selected items as badges */}
      {!renderBadgesInTrigger && selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedOptions.map((option) => (
            <Badge key={String(option.value)} variant="secondary" className="flex items-center gap-1">
              {option.label}
              {!disabled && (
                <button
                  type="button"
                  className="ring-offset-offwhite ml-1 rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onClick={() => handleRemove(option.value)}
                >
                  <IconCloseOutline className="size-3" />
                  <span className="sr-only">Remove</span>
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

// Form field wrapper component for easier integration with React Hook Form
interface MultiSelectComboboxFieldProps<
  T = string | number,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  label: string
  options: SelectOption<T>[]
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  renderBadgesInTrigger?: boolean
  disabled?: boolean
}

export function MultiSelectComboboxField<
  T extends string | number,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  className,
  renderBadgesInTrigger,
  disabled,
}: MultiSelectComboboxFieldProps<T, TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <MultiSelectCombobox
              options={options}
              selectedValues={field.value || []}
              onSelectionChange={field.onChange}
              placeholder={placeholder}
              searchPlaceholder={searchPlaceholder}
              emptyMessage={emptyMessage}
              renderBadgesInTrigger={renderBadgesInTrigger}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
